<template>
	<div class="container py-5">
        <h2>{{ $t('weightAverageCharts') }}</h2>
        <nav aria-label="breadcrumb" class="mb-4">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="`/modeling/${this.$route.params.id}/modelingresult/${this.$route.params.modelingResultId}`">
                        {{ $t('modelingResult') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ $t('weightAverageCharts') }}</li>
            </ol>
        </nav>
		<div v-if="pending">
            <div class="d-flex justify-content-center loading-center">
                <div class="spinner-border" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </div>
        </div>
        <div v-else>
            <div class="row mb-5" v-for="cfg in chartConfigs" :key="cfg.assetTypeId">
                <h5 class="mb-3">
                    {{ $t('assetType') }}: {{ cfg.assetTypeName }}
                </h5>
                <div class="col-md-8">
                    <apexchart
                        v-if="getSelectedChart(cfg)"
                        type="line"
                        width="800"
                        :height="500"
                        :options="getChartOptions(getSelectedChart(cfg))"
                        :series="getSelectedChart(cfg).series"
                        class="d-flex justify-content-center"
                    />
                </div>
                <div class="col-md-4">
                    <label>{{ $t('dataValues') }}</label>
                    <select class="form-select" v-model="cfg.selectedDataValue">
                        <option v-for="v in dataValues.filter(p => p.assetTypeId === cfg.assetTypeId)" :key="v.value" :value="v.value">
                            {{ v.name }}
                        </option>
                    </select>
                    <label>{{ $t('area') }}</label>
                    <select class="form-select" v-model="cfg.selectedGroupId" @change="cfg.selectedPackageId = null">
                        <option :value="null">{{ $t('allAreas') }}</option>
                        <option v-for="g in groups.filter(p => p.assetTypeId === cfg.assetTypeId)" :key="g.value" :value="Number(g.value)">
                            {{ g.name }}
                        </option>
                    </select>
                    <label>{{ $t('package') }}</label>
                    <select class="form-select" v-model="cfg.selectedPackageId" @change="cfg.selectedGroupId = null">
                        <option :value="null">{{ $t('allPackages') }}</option>
                        <option v-for="p in packages.filter(x => x.assetTypeId === cfg.assetTypeId)" :key="p.value" :value="Number(p.value)">
                            {{ p.name }}
                        </option>
                    </select>
                </div>
            </div>
            <div class="d-grid gap-2 d-md-flex mb-4">
                <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${this.$route.params.id}/modelingresult/${this.$route.params.modelingResultId}` })">{{ $t('back') }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import AnalysisApi from "../../api/server/analysis";
import ModelingApi from "../../api/server/modeling";
import CheckIfUserIsAdmin from "../../authCache";

export default {
    name: "resultCharts",
    watch: {
        $route: "requestData"
    },
    mounted() {
        this.requestData();
    },
    methods: {
        async requestData() {
            this.isUserAdmin = await CheckIfUserIsAdmin();

            var modeling = await ModelingApi.modelingDetail(this.$route.params.id);
            this.modelingName = modeling.name;

            var result = await AnalysisApi.weightedGraphCharts(this.$route.params.id, this.$route.params.modelingResultId, null, "");

            this.assetTypeList = result.assetTypes;
            this.dataValues = result.dataValues;
            this.groups = result.groups;      
            this.packages = result.packages; 

            this.chartConfigs = result.assetTypes.map(assetType => {
                return {
                    assetTypeId: assetType.assetTypeId,
                    assetTypeName: assetType.assetTypeName,
                    selectedDataValue: this.getSelectedDataValue(assetType.assetTypeId, result.dataValues),
                    selectedGroupId: null,
                    selectedPackageId: null,
                    charts: assetType.charts
                };
            });

            this.pending = false;
        },
        getSelectedDataValue(assetTypeId, dataValues) {
            return dataValues.find(v => v.selected && v.assetTypeId === assetTypeId)?.value || "performance";
        },
        formatAmount(value) {
            return value?.toFixed(2);
        },
        getSelectedChart(cfg) {
            const filteredCharts = cfg.charts.filter(c => c.dataValue === cfg.selectedDataValue);
            if (!filteredCharts.length) return null;

            // package chart
            if (cfg.selectedPackageId != null) {
                return filteredCharts.find(c => c.packageId === cfg.selectedPackageId) || null;
            }

            // group chart
            if (cfg.selectedGroupId != null) {
                return filteredCharts.find(c => c.groupId === cfg.selectedGroupId) || null;
            }

            // jinak All
            return filteredCharts.find(c => c.groupId == null && c.packageId == null) || null;
        },
        getChartOptions(chart) {
            return {
                chart: { type: "line", height: 500 },
                title: { text: this.$t("weightedAveragePerformanceOverTime") },
                xaxis: {
                    categories: chart.years,
                    title: { text: this.$t("year") },
                },
                yaxis: {
                    title: { text: this.$t("weightedAveragePerformance") },
                    labels: { formatter: (v) => this.formatAmount(v) },
                },
                tooltip: {
                    y: { formatter: (v) => this.formatAmount(v) },
                },
                dataLabels: { enabled: false },
                legend: { position: "top" },
            };
        }
    },
    data() {
        return {
            chartConfigs: [],
            dataValues: [],
            assetTypeList: [],
            groups: [],
            packages: [],
            pending: true,
            isUserAdmin: false,
            modelingName: ""
        }
    }
};
</script>